import { BOT_LEVEL_KNOBS, EXPERT_KNOBS, type BotLevelKnobs } from '../bot/levels.ts';
import type { LiveBotLevel } from '../bot/seats.ts';
import { dialsFromKnobs } from './dials.ts';
import { clampBotKnob } from './limits.ts';
import type { BotProfileDocument, BotProfileKnobs } from './types.ts';
import { CURRENT_BOT_PROFILE_VERSION } from './version.ts';

/** A level's preset, written out as profile knobs. */
export function presetProfileKnobs(level: LiveBotLevel): BotProfileKnobs {
  return profileKnobsFrom(BOT_LEVEL_KNOBS[level]);
}

/** Planner knobs as profile knobs: every field explicit, unset ones at the planner's default. */
export function profileKnobsFrom(knobs: BotLevelKnobs): BotProfileKnobs {
  return {
    depth: clampBotKnob('depth', knobs.depth),
    courseSense: knobs.courseSense ?? 'time',
    temperature: clampBotKnob('temperature', knobs.temperature ?? 0),
    aimErrorM: clampBotKnob('aimErrorM', knobs.aimErrorM ?? 0),
    brakeConfidence: clampBotKnob('brakeConfidence', knobs.brakeConfidence ?? 1),
    speedLimitKmh: clampBotKnob('speedLimitKmh', knobs.speedLimitKmh ?? 0),
    nudges: knobs.squeeze ?? knobs.anticipate ?? false,
    contactRoots: clampBotKnob('contactRoots', knobs.contactRoots ?? 0),
    forecastHulls: knobs.forecastHulls ?? false,
    followDistance: knobs.followDistance ?? false,
    aggression: clampBotKnob('aggression', knobs.aggression ?? 0),
    blockMaxCostTurns: clampBotKnob('blockMaxCostTurns', knobs.blockMaxCostTurns ?? 1),
    blockMinGainTurns: clampBotKnob('blockMinGainTurns', knobs.blockMinGainTurns ?? 0.5),
    width: clampBotKnob('width', knobs.width),
    density: clampBotKnob('density', knobs.density),
    rootDensity: clampBotKnob('rootDensity', knobs.rootDensity),
    verify: clampBotKnob('verify', knobs.verify),
  };
}

/** The planner knobs a profile races with: Expert's search internals plus every stored knob. */
export function knobsOf(knobs: BotProfileKnobs): BotLevelKnobs {
  return {
    ...EXPERT_KNOBS,
    depth: knobs.depth,
    width: knobs.width,
    density: knobs.density,
    rootDensity: knobs.rootDensity,
    verify: knobs.verify,
    courseSense: knobs.courseSense,
    temperature: knobs.temperature,
    aimErrorM: knobs.aimErrorM,
    brakeConfidence: knobs.brakeConfidence,
    speedLimitKmh: knobs.speedLimitKmh,
    // Reads rivals whenever something uses the forecast, so Manners works without squeezing.
    anticipate: knobs.nudges || knobs.forecastHulls || knobs.followDistance || knobs.aggression > 0,
    squeeze: knobs.nudges,
    contactRoots: knobs.contactRoots,
    forecastHulls: knobs.forecastHulls,
    followDistance: knobs.followDistance,
    aggression: knobs.aggression,
    blockMaxCostTurns: knobs.blockMaxCostTurns,
    blockMinGainTurns: knobs.blockMinGainTurns,
  };
}

/** A new, unnamed profile that starts as `from`'s preset, with the bars where that preset sits. */
export function createBotProfile(from: LiveBotLevel = 'expert'): BotProfileDocument {
  const knobs = presetProfileKnobs(from);

  return {
    version: CURRENT_BOT_PROFILE_VERSION,
    name: '',
    basedOn: from,
    dials: dialsFromKnobs(knobs),
    knobs,
  };
}
